import { getCookie } from "cookies-next";

import { guestRequestEstimate, guestRequestQuote } from "~/api/contracts";
import { ListingData } from "~/context/ListingPageDataContext";
import {
  BookListingEstimate,
  BookListingQuote,
  IGuestRequestQuote,
  IBooking,
} from "~/types/api-types";
import { ACCESS_TOKEN_COOKIE } from "~/utils/constants";

import { getFsvOverDurationState } from "./fsv-over-duration.utils";

import { BookingScheduleDay } from "@/lib/types/schedule-types";

const createQuoteFromContractsResponse = (
  response: IGuestRequestQuote,
): BookListingQuote => {
  if (!response) {
    return {
      masterContract: undefined,
      splitPaymentPermitted: false,
      spaceRentalContract: undefined,
      addOnsContracts: [],
      guestPaymentDetails: undefined,
      paymentSchedules: {},
      payLaterTestBucket: false,
    };
  }

  const spaceRentalContract = (response?.children || []).find(
    (item) => item.type === "SPACE_RENTAL",
  );
  const addOnsContracts = (response?.children || []).filter(
    (contract) => contract.type === "ITEM",
  );
  const quote = {
    masterContract: response?.master,
    splitPaymentPermitted: response?.split_payment_permitted,
    guestPaymentDetails: response?.guest_payment_method_details,
    spaceRentalContract,
    addOnsContracts,
    paymentSchedules: response?.payment_schedules,
    payLaterTestBucket: response?.pay_later_test_bucket,
  };

  return quote;
};

interface GetPricingInput {
  schedule: BookingScheduleDay[];
  attendeePricingTierMax: number;
  hostId: string;
  guestId: string;
  listingId: string;
  spaceId: string;
  listingPrice: number;
  gaClientId: string;
  locale: string;
  enforceMinimumHours: boolean;
  paymentOption: string;
  /** Statsig variant for guest_fee_discount_25_50: "full" | "25_percent_off" | "50_percent_off" */
  guestFeeTestGroup: string;
}

const fetchBookingQuote = async (
  input: GetPricingInput,
): Promise<BookListingQuote> => {
  const accessToken = getCookie(ACCESS_TOKEN_COOKIE) ?? undefined;
  const bookingData = {
    type: "BOOKING",
    info: {
      host_id: input.hostId,
      guest_id: input.guestId,
      schedule: input.schedule,
      rate: input.listingPrice,
      duration_type: "HOURLY_RATE",
      listing_id: input.listingId,
      space_id: input.spaceId,
      prepare_only: true,
      ga_client_id: input.gaClientId,
      number_of_guests: input.attendeePricingTierMax,
    },
  };

  const payload = {
    host_sso_id: input.hostId,
    guest_sso_id: input.guestId,
    sales_tax_disable: true,
    type: "REQUEST",
    payment_option: input.paymentOption,
    enforce_minimum_hours: input.enforceMinimumHours,
    guest_fee_test_group: input.guestFeeTestGroup,
    items: [bookingData],
  };
  const response = await guestRequestQuote({
    accessToken: accessToken as string,
    locale: input.locale,
    payload,
  });

  const quote = createQuoteFromContractsResponse(response);
  return quote;
};

const fetchBookingEstimate = async ({
  listingId,
  listingCountry,
}: {
  listingId: string;
  listingCountry: string;
}): Promise<BookListingEstimate> => {
  const response = await guestRequestEstimate({
    listing_ids: [listingId],
    country: listingCountry,
  });

  return response?.[listingId];
};

const getCleaningFee = (quote: BookListingQuote | undefined) => {
  const addtlCharges = quote?.masterContract?.calculations?.addtl_charges;
  const cleaningFee = (addtlCharges || []).find(
    (charge) => charge.type === "CLEANING_FEE",
  );
  return cleaningFee?.charge_amount;
};

interface IsInstantBookOfferedParams {
  booking: IBooking | undefined;
  listingData: ListingData;
}

/**
 * Whether Instant Book still applies to the current selection. A full-service
 * reservation longer than the host package needs a pricing update from the
 * venue, so it drops to the request flow even on an Instant Book listing.
 * Hourly listings keep Instant Book when the guest books extra hours.
 */
const getIsInstantBookOffered = ({
  booking,
  listingData,
}: IsInstantBookOfferedParams): boolean => {
  const { exceedsMinDuration } = getFsvOverDurationState({
    isFullServiceVenue: listingData.fullService?.active,
    minBookingDurationHours:
      listingData.fullService?.min_booking_duration_hours,
    selectedDurationHours: booking?.duration,
  });

  return !!listingData.isInstantBookActive && !exceedsMinDuration;
};

interface GetButtonTextParams {
  quote: BookListingQuote | undefined;
  booking: IBooking | undefined;
  listingData: ListingData;
  intl: {
    formatMessage: (descriptor: { defaultMessage: string }) => string;
  };
}

const getBookListingButtonText = ({
  quote,
  booking,
  listingData,
  intl,
}: GetButtonTextParams): string => {
  const isInstantBook =
    getIsInstantBookOffered({ booking, listingData }) &&
    (quote ? (booking?.is_instant_booking ?? false) : true);

  if (isInstantBook) {
    return intl.formatMessage({
      defaultMessage: "Book Now",
    });
  }

  if (quote) {
    return intl.formatMessage({
      defaultMessage: "Request to book",
    });
  }

  return intl.formatMessage({
    defaultMessage: "Start Booking",
  });
};

export {
  createQuoteFromContractsResponse,
  fetchBookingEstimate,
  fetchBookingQuote,
  getCleaningFee,
  getBookListingButtonText,
  getIsInstantBookOffered,
  type GetPricingInput,
  type GetButtonTextParams,
};
